import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { CalendarClock, Plus, Trash2, ClipboardCheck, CloudUpload, Bell, Send, ShieldCheck, Loader2, FileText, Mic, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TIMELINE_KEY = "pod_timeline_v1";
const CHECK_KEY = "pod_checklist_v1";

const makeId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function loadTimeline() {
  try { return JSON.parse(localStorage.getItem(TIMELINE_KEY) || "[]"); } catch { return []; }
}
function saveTimeline(items) { localStorage.setItem(TIMELINE_KEY, JSON.stringify(items)); }
function loadChecks() {
  try { return JSON.parse(localStorage.getItem(CHECK_KEY) || "{}"); } catch { return {}; }
}
function saveChecks(o) { localStorage.setItem(CHECK_KEY, JSON.stringify(o)); }

export default function MyCase() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [checklist, setChecklist] = useState(null);
  const [checks, setChecks] = useState(() => loadChecks());
  const [events, setEvents] = useState(() => loadTimeline());
  const [eDate, setEDate] = useState("");
  const [eLabel, setELabel] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/checklist?lang=${lang}`).then((r) => setChecklist(r.data));
  }, [lang]);

  // Cloud pull when authed — merge (don't lose anonymous work)
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/case/state");
        const cloudChecks = data.checks || {};
        const cloudEvents = Array.isArray(data.events) ? data.events : [];
        const cloudHasData = Object.keys(cloudChecks).length || cloudEvents.length;
        if (cloudHasData) {
          // merge checks: cloud wins on conflicts
          const mergedChecks = { ...checks, ...cloudChecks };
          // merge events by id, preferring cloud entries
          const byId = new Map();
          [...events, ...cloudEvents].forEach((e) => byId.set(e.id, e));
          const mergedEvents = Array.from(byId.values()).sort((a, b) => a.date.localeCompare(b.date));
          setChecks(mergedChecks); saveChecks(mergedChecks);
          setEvents(mergedEvents); saveTimeline(mergedEvents);
          api.put("/case/state", { checks: mergedChecks, events: mergedEvents }).catch((err) => console.warn("case cloud sync failed", err));
        } else {
          // cloud empty — push local up
          api.put("/case/state", { checks, events }).catch((err) => console.warn("case cloud sync failed", err));
        }
      } catch (err) {
        console.warn("case state pull failed", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const cloudSave = (nextChecks, nextEvents) => {
    if (!user || typeof user !== "object") return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/case/state", { checks: nextChecks, events: nextEvents }).catch((err) => console.warn("case cloud sync failed", err));
    }, 400);
  };

  // ---- Reminder settings (signed-in only) ----
  const [rmPhone, setRmPhone] = useState("");
  const [rmLang, setRmLang] = useState("en");
  const [rmConsent, setRmConsent] = useState(false);
  const [rmVerifiedPhone, setRmVerifiedPhone] = useState("");
  const [rmSaving, setRmSaving] = useState(false);
  const [rmTesting, setRmTesting] = useState(false);
  const [rmSending, setRmSending] = useState(false);
  const [rmChecking, setRmChecking] = useState(false);
  const [rmVerifyStep, setRmVerifyStep] = useState("idle"); // idle | sent
  const [rmCode, setRmCode] = useState("");
  const rmPhoneVerified = !!rmPhone && rmVerifiedPhone === rmPhone;
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    api.get("/reminders/settings")
      .then((r) => {
        setRmPhone(r.data.reminder_phone || "");
        setRmLang(r.data.reminder_lang || lang || "en");
        setRmConsent(!!r.data.sms_consent);
        setRmVerifiedPhone(r.data.verified_phone || "");
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const sendVerify = async () => {
    setRmSending(true);
    try {
      await api.post("/reminders/verify/start", { reminder_phone: rmPhone });
      setRmVerifyStep("sent");
      toast.success(t("mycase.verify_sent"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setRmSending(false); }
  };
  const checkVerify = async () => {
    setRmChecking(true);
    try {
      const res = await api.post("/reminders/verify/check", { reminder_phone: rmPhone, code: rmCode });
      setRmVerifiedPhone(res.data.verified_phone || rmPhone);
      setRmVerifyStep("idle");
      setRmCode("");
      toast.success(t("mycase.verify_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("mycase.verify_error"));
    } finally { setRmChecking(false); }
  };
  const saveReminders = async () => {
    if (rmPhone && !rmConsent) {
      toast.error(t("mycase.reminders_consent_required"));
      return;
    }
    setRmSaving(true);
    try {
      await api.put("/reminders/settings", { reminder_phone: rmPhone, reminder_lang: rmLang, sms_consent: rmConsent });
      toast.success(t("mycase.reminders_saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setRmSaving(false); }
  };
  const testReminder = async () => {
    setRmTesting(true);
    try {
      await api.post("/reminders/test", {});
      toast.success(t("mycase.reminders_sent"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test SMS failed");
    } finally { setRmTesting(false); }
  };

  const toggle = (key) => {
    const next = { ...checks, [key]: !checks[key] };
    setChecks(next);
    saveChecks(next);
    cloudSave(next, events);
  };

  const addEvent = () => {
    if (!eDate || !eLabel.trim()) return;
    const next = [...events, { id: makeId(), date: eDate, label: eLabel.trim() }].sort((a, b) => a.date.localeCompare(b.date));
    setEvents(next);
    saveTimeline(next);
    cloudSave(checks, next);
    setEDate(""); setELabel("");
  };
  const removeEvent = (id) => {
    const next = events.filter((e) => e.id !== id);
    setEvents(next);
    saveTimeline(next);
    cloudSave(checks, next);
  };

  const signedIn = user && typeof user === "object";

  // Countdown to the next upcoming timeline event
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextEvent = [...events]
    .filter((e) => e.date)
    .sort((a, b) => a.date.localeCompare(b.date))
    .find((e) => {
      const d = new Date(e.date + "T12:00:00");
      d.setHours(0, 0, 0, 0);
      return d >= today;
    });
  let countdown = null;
  if (nextEvent) {
    const d = new Date(nextEvent.date + "T12:00:00");
    d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / (1000 * 60 * 60 * 24));
    const label =
      days === 0 ? t("mycase.countdown_today")
      : days === 1 ? t("mycase.countdown_tomorrow")
      : `${t("mycase.countdown_days_prefix")} ${days} ${t("mycase.countdown_days_suffix")}`;
    const urgency = days <= 3 ? "urgent" : days <= 10 ? "warning" : "calm";
    countdown = { label, days, event: nextEvent, urgency };
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("mycase.title")}</h1>
          <p className="text-[#4A525A]">{t("mycase.subtitle")}</p>
        </div>
        {signedIn ? (
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-300 text-xs font-bold px-2.5 py-1 rounded-full" data-testid="cloud-status">
            <CloudUpload className="w-3.5 h-3.5" /> {t("learn.synced")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-300 text-xs font-bold px-2.5 py-1 rounded-full" data-testid="cloud-status">
            {lang === "es" ? "Solo en este dispositivo" : "On this device only"}
          </span>
        )}
      </div>

      <section data-testid="mycase-shortcuts">
        {countdown && (
          <div
            data-testid="mycase-countdown"
            className={`mb-4 rounded-2xl p-4 sm:p-5 flex items-center gap-4 border-2 shadow-sm ${
              countdown.urgency === "urgent"
                ? "bg-[#C85A32] border-[#B24C28] text-white"
                : countdown.urgency === "warning"
                  ? "bg-amber-50 border-amber-300 text-amber-900"
                  : "bg-[#2C4A3E] border-[#1F382E] text-white"
            }`}
          >
            <CalendarClock className={`w-8 h-8 shrink-0 ${countdown.urgency === "warning" ? "text-amber-700" : "text-white"}`} />
            <div className="min-w-0 flex-1">
              <div className={`text-[10px] font-extrabold uppercase tracking-[0.25em] ${countdown.urgency === "warning" ? "text-amber-800" : "text-white/85"}`}>
                {new Date(countdown.event.date + "T12:00:00").toLocaleDateString(lang === "es" ? "es-US" : "en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight" data-testid="mycase-countdown-label">
                {countdown.label}
              </div>
              <div className={`mt-0.5 text-sm sm:text-base leading-snug ${countdown.urgency === "warning" ? "text-amber-900" : "text-white/90"}`}>
                {countdown.event.label}
              </div>
            </div>
          </div>
        )}
        <h2 className="text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C85A32]" /> {t("mycase.shortcuts_title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {[
            { to: "/summarize", icon: FileText, key: "summarize", testid: "shortcut-summarize" },
            { to: "/practice", icon: Mic, key: "practice", testid: "shortcut-practice" },
          ].map(({ to, icon: Icon, key, testid }) => {
            const usage = (signedIn && user.usage) ? user.usage : {};
            // Uploads limit for summarize is tier-based; practice is unlimited on every tier.
            const rawLimit = signedIn ? (user.limits?.uploads ?? user.free_limit ?? 10) : 10;
            const isUnlimitedUploads = rawLimit < 0;
            const isPracticeFeature = key === "practice";
            const showUnlimited = isPracticeFeature || isUnlimitedUploads;
            const limit = rawLimit < 0 ? 10 : rawLimit;
            const remaining = Math.max(0, limit - (usage[key] || 0));
            return (
              <Link
                key={key}
                to={to}
                data-testid={testid}
                className="group bg-white border-2 border-[#E4DDD3] hover:border-[#C85A32] rounded-2xl p-5 flex flex-col gap-3 transition-colors pod-focus"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#FAF5EC] border-2 border-[#E4DDD3] flex items-center justify-center text-[#C85A32] shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  {signedIn && (
                    showUnlimited ? (
                      <span className="text-[10px] font-extrabold uppercase tracking-widest bg-[#2C4A3E] text-white px-2 py-0.5 rounded-full" data-testid={`${testid}-premium`}>
                        {t("mycase.shortcut_unlimited")}
                      </span>
                    ) : (
                      <span
                        className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border-2 ${
                          remaining === 0
                            ? "bg-amber-50 text-amber-800 border-amber-300"
                            : "bg-[#FAF5EC] text-[#C85A32] border-[#E4DDD3]"
                        }`}
                        data-testid={`${testid}-quota`}
                      >
                        {remaining}/{limit} {t("mycase.shortcut_free_left")}
                      </span>
                    )
                  )}
                </div>
                <div>
                  <div className="text-lg font-bold text-[#1C2024]">{t(`mycase.shortcut_${key}_title`)}</div>
                  <p className="mt-1 text-sm text-[#4A525A]">{t(`mycase.shortcut_${key}_desc`)}</p>
                </div>
                <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-[#C85A32] group-hover:translate-x-0.5 transition-transform">
                  {t("mycase.shortcut_open")} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-[#C85A32]" /> {t("mycase.checklist")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {checklist && Object.entries(checklist).map(([bucket, items]) => (
            <div key={bucket} className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">
                {t(`mycase.${bucket === "7_days_before" ? "7d" : bucket === "24_hours_before" ? "24h" : bucket === "court_day" ? "court_day" : "after_court"}`)}
              </div>
              <ul className="mt-3 space-y-2">
                {items.map((it, idx) => {
                  const k = `${bucket}-${idx}`;
                  return (
                    <li key={k} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={!!checks[k]}
                        onChange={() => toggle(k)}
                        data-testid={`check-${k}`}
                        className="mt-1.5 w-5 h-5 accent-[#C85A32]"
                      />
                      <span className={`leading-relaxed ${checks[k] ? "line-through text-[#707880]" : ""}`}>{it}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#C85A32]" /> {t("mycase.timeline")}</h2>
        <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-3">
            <input
              type="date"
              data-testid="timeline-date"
              value={eDate}
              onChange={(e) => setEDate(e.target.value)}
              className="rounded-xl border-2 border-[#E4DDD3] p-3"
            />
            <input
              type="text"
              data-testid="timeline-label"
              value={eLabel}
              onChange={(e) => setELabel(e.target.value)}
              placeholder={lang === "es" ? "¿Qué pasó? (ej., Recibí citación)" : "What happened? (e.g., Received summons)"}
              className="rounded-xl border-2 border-[#E4DDD3] p-3"
            />
            <button
              onClick={addEvent}
              data-testid="timeline-add"
              className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold rounded-xl px-4 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> {t("mycase.add_event")}
            </button>
          </div>
          <ul className="mt-5 space-y-3">
            {events.length === 0 && <li className="text-[#4A525A] text-sm">{t("mycase.no_events")}</li>}
            {events.map((ev) => (
              <li key={ev.id} data-testid={`timeline-item-${ev.id}`} className="flex items-center gap-3 border-l-4 border-[#C85A32] bg-[#FAF5EC] rounded-r-xl px-4 py-3">
                <div className="mono text-sm text-[#4A525A] w-28 shrink-0">{ev.date}</div>
                <div className="flex-1 font-medium">{ev.label}</div>
                <button onClick={() => removeEvent(ev.id)} data-testid={`timeline-remove-${ev.id}`} className="pod-focus text-[#4A525A] hover:text-red-600 p-2 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {signedIn && (
        <section data-testid="reminders-section">
          <h2 className="text-xl font-bold flex items-center gap-2"><Bell className="w-5 h-5 text-[#C85A32]" /> {t("mycase.reminders_title")}</h2>
          <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 mt-3 space-y-4">
            <p className="text-sm text-[#4A525A]">{t("mycase.reminders_desc")}</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("mycase.reminders_phone")}</label>
              <input
                type="tel"
                data-testid="reminders-phone"
                value={rmPhone}
                onChange={(e) => { setRmPhone(e.target.value); setRmVerifyStep("idle"); setRmCode(""); }}
                placeholder="+15551234567"
                className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              />
              {rmPhone && (
                <div className="mt-2">
                  {rmPhoneVerified ? (
                    <div className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-full px-2.5 py-1" data-testid="phone-verified-badge">
                      <ShieldCheck className="w-3.5 h-3.5" /> {t("mycase.verify_verified")}
                    </div>
                  ) : rmVerifyStep === "sent" ? (
                    <div className="space-y-2">
                      <p className="text-xs text-[#4A525A]">{t("mycase.verify_pending")} <span className="font-semibold">{rmPhone}</span>.</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          data-testid="verify-code"
                          value={rmCode}
                          onChange={(e) => setRmCode(e.target.value.replace(/\D/g, ""))}
                          placeholder={t("mycase.verify_code")}
                          className="flex-1 rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-2.5 bg-white tracking-widest font-mono"
                        />
                        <button
                          onClick={checkVerify}
                          disabled={rmChecking || rmCode.length !== 6}
                          data-testid="verify-check"
                          className="pod-focus bg-[#2C4A3E] hover:bg-[#1F382E] disabled:opacity-50 text-white font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
                        >
                          {rmChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                          {t("mycase.verify_check")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-amber-800">{t("mycase.verify_needed")}</span>
                      <button
                        onClick={sendVerify}
                        disabled={rmSending || !rmPhone.startsWith("+")}
                        data-testid="verify-send"
                        className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-50 text-white text-sm font-bold py-2 px-3 rounded-xl inline-flex items-center gap-2"
                      >
                        {rmSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {t("mycase.verify_send")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("mycase.reminders_lang")}</label>
              <select
                data-testid="reminders-lang"
                value={rmLang}
                onChange={(e) => setRmLang(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <label className="flex items-start gap-3 bg-[#FAF5EC] border border-[#E4DDD3] rounded-xl p-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="reminders-consent"
                checked={rmConsent}
                onChange={(e) => setRmConsent(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#C85A32]"
              />
              <span className="text-sm text-[#1C2024] leading-snug">{t("mycase.reminders_consent")}</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={saveReminders}
                disabled={rmSaving || (rmPhone && (!rmConsent || !rmPhoneVerified))}
                data-testid="reminders-save"
                className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-50 text-white font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
              >
                {t("mycase.reminders_save")}
              </button>
              <button
                onClick={testReminder}
                disabled={rmTesting || !rmPhone || !rmConsent || !rmPhoneVerified}
                data-testid="reminders-test"
                className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] disabled:opacity-50 text-[#1C2024] font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
              >
                <Send className="w-4 h-4" /> {t("mycase.reminders_test")}
              </button>
            </div>
            {!rmPhone && <p className="text-xs text-[#707880]">{t("mycase.reminders_disabled")}</p>}
          </div>
        </section>
      )}
    </div>
  );
}
